// src/components/admin/reports/VolunteerReport.jsx
import React, { useState, useEffect } from 'react';
import {
  getVolunteers,
  getShifts,
  getShiftAssignments,
  generateHoursCSV,
  MILESTONES,
  fmtDate,
} from '../../../api/communityApi';

const parseTs = (ts) => {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate();
  return new Date(ts);
};

const isInMonth = (dateStr, year, month) => {
  if (!dateStr) return false;
  const [y, m] = dateStr.split('-').map(Number);
  return y === year && m === month + 1;
};

const downloadCSV = (content, filename) => {
  const blob = new Blob([content], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

export default function VolunteerReport({ orgId }) {
  const [activeTab,   setActiveTab]   = useState('hours');
  const [volunteers,  setVolunteers]  = useState([]);
  const [shifts,      setShifts]      = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');

  // Shift Attendance tab state
  const now = new Date();
  const [filterYear,  setFilterYear]  = useState(now.getFullYear());
  const [filterMonth, setFilterMonth] = useState(now.getMonth());
  const [shiftData,   setShiftData]   = useState([]); // enriched shift rows
  const [shiftLoading, setShiftLoading] = useState(false);

  useEffect(() => {
    loadBase();
  }, [orgId]);

  const loadBase = async () => {
    setLoading(true);
    setError('');
    try {
      const [v, s] = await Promise.all([
        getVolunteers(orgId),
        getShifts(orgId),
      ]);
      setVolunteers(v);
      setShifts(s);
    } catch (e) {
      setError('Failed to load volunteer data: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const loadShiftAttendance = async () => {
    setShiftLoading(true);
    try {
      const monthShifts = shifts.filter(s => isInMonth(s.date, filterYear, filterMonth));
      const enriched = await Promise.all(
        monthShifts.map(async (shift) => {
          const assignments = await getShiftAssignments(orgId, shift.id);
          const signedUp    = assignments.length;
          const checkedIn   = assignments.filter(a => a.status === 'checked_in' || a.status === 'completed').length;
          const noShows     = assignments.filter(a => a.status === 'no_show').length;
          const fillRate    = shift.maxSlots > 0 ? Math.round((signedUp / shift.maxSlots) * 100) : 0;
          return { ...shift, signedUp, checkedIn, noShows, fillRate };
        })
      );
      setShiftData(enriched);
    } catch (e) {
      console.error('Failed to load shift attendance:', e);
    } finally {
      setShiftLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'shifts' && shifts.length > 0) {
      loadShiftAttendance();
    }
  }, [activeTab, filterYear, filterMonth, shifts]);

  // ── Hours Summary computations ──────────────────────────────────────────────
  const sortedVols    = [...volunteers].sort((a, b) => (b.totalHours || 0) - (a.totalHours || 0));
  const totalHours    = volunteers.reduce((s, v) => s + (v.totalHours || 0), 0);
  const avgHours      = volunteers.length > 0 ? (totalHours / volunteers.length).toFixed(1) : 0;
  const zeroHoursVols = volunteers.filter(v => !v.totalHours || v.totalHours === 0).length;

  const handleExportHours = () => {
    const rows = sortedVols.map(v => ({
      Name: v.displayName || `${v.firstName || ''} ${v.lastName || ''}`.trim(),
      Status: v.status || '',
      'Total Hours': v.totalHours || 0,
      Milestones: (v.earnedMilestones || []).join(', '),
      'Last Active': fmtDate(v.updatedAt || v.createdAt),
    }));
    const keys = Object.keys(rows[0] || {});
    const header = keys.join(',');
    const lines  = rows.map(r => keys.map(k => `"${String(r[k] ?? '').replace(/"/g, '""')}"`).join(','));
    downloadCSV([header, ...lines].join('\n'), 'volunteer-hours.csv');
  };

  // ── Milestone computations ─────────────────────────────────────────────────
  const milestoneStats = MILESTONES.map(m => {
    if (m.threshold) {
      const earned = volunteers.filter(v => (v.totalHours || 0) >= m.threshold);
      return { ...m, count: earned.length, volunteers: earned };
    } else {
      // 1-year milestone
      const earned = volunteers.filter(v => (v.earnedMilestones || []).includes('1year'));
      return { ...m, count: earned.length, volunteers: earned };
    }
  });

  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  if (loading) {
    return (
      <div className="bb-panel">
        <div className="bb-panel-body" style={{ textAlign: 'center', padding: 40 }}>
          <div className="bb-spinner" />
          <p style={{ marginTop: 12, color: '#9CA3AF' }}>Loading volunteer data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bb-panel">
      <div className="bb-panel-header">
        <h3 className="bb-panel-title">Volunteer Report</h3>
        <p style={{ fontSize: 13, color: '#6B7280', marginTop: 4 }}>
          Hours, shift attendance, and milestone tracking for {volunteers.length} volunteers.
        </p>
      </div>

      {error && <div className="bb-alert bb-alert-error" style={{ margin: '0 20px 12px' }}>{error}</div>}

      {/* Sub-tabs */}
      <div className="bb-rpt-subtabs" style={{ margin: '0 20px' }}>
        {[
          { id: 'hours',      label: 'Hours Summary'    },
          { id: 'milestones', label: 'Milestone Tracker' },
        ].map(t => (
          <button
            key={t.id}
            className={`bb-rpt-subtab${activeTab === t.id ? ' active' : ''}`}
            onClick={() => setActiveTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="bb-panel-body">

        {/* ── HOURS SUMMARY ── */}
        {activeTab === 'hours' && (
          <div>
            {/* Stat cards */}
            <div className="bb-rpt-stats" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: 20 }}>
              <div className="bb-rpt-stat">
                <div className="bb-rpt-stat-val">{totalHours.toFixed(1)}</div>
                <div className="bb-rpt-stat-lbl">Total Hours</div>
              </div>
              <div className="bb-rpt-stat">
                <div className="bb-rpt-stat-val">{avgHours}</div>
                <div className="bb-rpt-stat-lbl">Avg Hours / Volunteer</div>
              </div>
              <div className="bb-rpt-stat">
                <div className="bb-rpt-stat-val" style={{ color: zeroHoursVols > 0 ? '#EF4444' : '#4338CA' }}>
                  {zeroHoursVols}
                </div>
                <div className="bb-rpt-stat-lbl">Volunteers with 0 Hours</div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
              <button className="bb-btn bb-btn-secondary" onClick={handleExportHours}>
                Export Hours CSV
              </button>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table className="bb-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Status</th>
                    <th>Total Hours</th>
                    <th>Milestones</th>
                    <th>Last Active</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedVols.map((v, i) => {
                    const name       = v.displayName || `${v.firstName || ''} ${v.lastName || ''}`.trim() || 'Unknown';
                    const milestones = v.earnedMilestones || [];
                    return (
                      <tr key={v.id}>
                        <td style={{ color: '#9CA3AF', fontSize: 12 }}>{i + 1}</td>
                        <td style={{ fontWeight: 500 }}>{name}</td>
                        <td>
                          <span style={{
                            fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                            background: v.status === 'active' ? '#D1FAE5' : '#F3F4F6',
                            color: v.status === 'active' ? '#065F46' : '#374151'
                          }}>
                            {v.status || 'unknown'}
                          </span>
                        </td>
                        <td style={{ fontWeight: 600, color: '#4338CA' }}>{v.totalHours || 0}h</td>
                        <td>
                          {milestones.length === 0 ? (
                            <span style={{ color: '#9CA3AF', fontSize: 12 }}>—</span>
                          ) : (
                            <span style={{ fontSize: 13, letterSpacing: '0.1em' }}>
                              {milestones.includes('200hrs') && '◆ '}
                              {milestones.includes('100hrs') && '★ '}
                              {milestones.includes('50hrs')  && '◉ '}
                              {milestones.includes('10hrs')  && '◦ '}
                              {milestones.includes('1year')  && '✦'}
                            </span>
                          )}
                        </td>
                        <td style={{ fontSize: 12, color: '#6B7280' }}>
                          {fmtDate(v.updatedAt || v.createdAt)}
                        </td>
                      </tr>
                    );
                  })}
                  {sortedVols.length === 0 && (
                    <tr>
                      <td colSpan={6} style={{ textAlign: 'center', color: '#9CA3AF' }}>No volunteers found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── SHIFT ATTENDANCE ── */}
        {activeTab === 'shifts' && (
          <div>
            {/* Month filter */}
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 20 }}>
              <label className="bb-label" style={{ marginBottom: 0 }}>Filter by Month:</label>
              <select
                className="bb-input"
                value={filterMonth}
                onChange={e => setFilterMonth(Number(e.target.value))}
                style={{ width: 120 }}
              >
                {MONTHS.map((m, i) => (
                  <option key={i} value={i}>{m}</option>
                ))}
              </select>
              <select
                className="bb-input"
                value={filterYear}
                onChange={e => setFilterYear(Number(e.target.value))}
                style={{ width: 90 }}
              >
                {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>

            {shiftLoading ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <div className="bb-spinner" />
                <p style={{ marginTop: 12, color: '#9CA3AF' }}>Loading shift data...</p>
              </div>
            ) : (
              <>
                {/* Summary stats */}
                {shiftData.length > 0 && (
                  <div className="bb-rpt-stats" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: 20 }}>
                    <div className="bb-rpt-stat">
                      <div className="bb-rpt-stat-val">{shiftData.length}</div>
                      <div className="bb-rpt-stat-lbl">Total Shifts</div>
                    </div>
                    <div className="bb-rpt-stat">
                      <div className="bb-rpt-stat-val">
                        {shiftData.reduce((s, r) => s + r.checkedIn, 0)}
                      </div>
                      <div className="bb-rpt-stat-lbl">Total Check-ins</div>
                    </div>
                    <div className="bb-rpt-stat">
                      <div className="bb-rpt-stat-val">
                        {shiftData.length > 0
                          ? Math.round(shiftData.reduce((s, r) => s + r.fillRate, 0) / shiftData.length)
                          : 0}%
                      </div>
                      <div className="bb-rpt-stat-lbl">Avg Fill Rate</div>
                    </div>
                  </div>
                )}

                <div style={{ overflowX: 'auto' }}>
                  <table className="bb-table">
                    <thead>
                      <tr>
                        <th>Shift Title</th>
                        <th>Date</th>
                        <th>Category</th>
                        <th>Max Slots</th>
                        <th>Signed Up</th>
                        <th>Checked In</th>
                        <th>No-Shows</th>
                        <th>Fill Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shiftData.map(row => (
                        <tr key={row.id}>
                          <td style={{ fontWeight: 500 }}>{row.title}</td>
                          <td style={{ fontSize: 12 }}>{row.date || '—'}</td>
                          <td style={{ textTransform: 'capitalize', fontSize: 12 }}>
                            {(row.category || '').replace(/_/g, ' ')}
                          </td>
                          <td>{row.maxSlots || row.maxVolunteers || '—'}</td>
                          <td>{row.signedUp}</td>
                          <td style={{ color: '#065F46', fontWeight: 600 }}>{row.checkedIn}</td>
                          <td style={{ color: row.noShows > 0 ? '#991B1B' : '#374151' }}>{row.noShows}</td>
                          <td style={{ minWidth: 120 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div style={{
                                flex: 1, height: 8, background: '#E5E7EB',
                                borderRadius: 4, overflow: 'hidden'
                              }}>
                                <div style={{
                                  width: `${row.fillRate}%`, height: '100%',
                                  background: row.fillRate >= 80 ? '#4338CA' : row.fillRate >= 50 ? '#7C3AED' : '#A78BFA',
                                  borderRadius: 4
                                }} />
                              </div>
                              <span style={{ fontSize: 12, fontWeight: 600, minWidth: 32 }}>{row.fillRate}%</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {shiftData.length === 0 && (
                        <tr>
                          <td colSpan={8} style={{ textAlign: 'center', color: '#9CA3AF', padding: 24 }}>
                            No shifts found for {MONTHS[filterMonth]} {filterYear}.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── MILESTONE TRACKER ── */}
        {activeTab === 'milestones' && (
          <div>
            <style>{`
              @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700&family=Playfair+Display:wght@700;800&display=swap');
              .ms-card-name { font-family: 'Cormorant Garamond', 'Playfair Display', Georgia, serif; }
            `}</style>

            {/* Milestone stat cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 32 }}>
              {milestoneStats.map((m, idx) => {
                const palettes = [
                  { bg: 'linear-gradient(145deg,#D1FAE5 0%,#A7F3D0 100%)', border: '#6EE7B7', iconBg: '#10B981', iconShadow: 'rgba(16,185,129,0.35)', nameColor: '#065F46', rangeColor: '#047857' },
                  { bg: 'linear-gradient(145deg,#DBEAFE 0%,#BFDBFE 100%)', border: '#93C5FD', iconBg: '#3B82F6', iconShadow: 'rgba(59,130,246,0.35)', nameColor: '#1E3A8A', rangeColor: '#1D4ED8' },
                  { bg: 'linear-gradient(145deg,#FEF3C7 0%,#FDE68A 100%)', border: '#FCD34D', iconBg: '#F59E0B', iconShadow: 'rgba(245,158,11,0.35)',  nameColor: '#78350F', rangeColor: '#92400E' },
                  { bg: 'linear-gradient(145deg,#FEE2E2 0%,#FECACA 100%)', border: '#FCA5A5', iconBg: '#EF4444', iconShadow: 'rgba(239,68,68,0.35)',   nameColor: '#7F1D1D', rangeColor: '#991B1B' },
                  { bg: 'linear-gradient(145deg,#EDE9FE 0%,#DDD6FE 100%)', border: '#C4B5FD', iconBg: '#8B5CF6', iconShadow: 'rgba(139,92,246,0.35)', nameColor: '#3B0764', rangeColor: '#5B21B6' },
                ];
                const pal = palettes[idx] || palettes[0];
                return (
                  <div key={m.key} style={{
                    background: pal.bg,
                    border: `1.5px solid ${pal.border}`,
                    borderRadius: 18,
                    padding: '24px 16px 20px',
                    textAlign: 'center',
                    boxShadow: `0 4px 18px ${pal.iconShadow}`,
                    position: 'relative',
                    overflow: 'hidden',
                  }}>
                    {/* Decorative circle behind icon */}
                    <div style={{
                      width: 64, height: 64, borderRadius: '50%',
                      background: pal.iconBg,
                      boxShadow: `0 6px 20px ${pal.iconShadow}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      margin: '0 auto 14px',
                    }}>
                      <span style={{ fontSize: 30, lineHeight: 1, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }}>{m.icon}</span>
                    </div>

                    {/* Elegant name */}
                    <div className="ms-card-name" style={{
                      fontSize: 22, fontWeight: 700, letterSpacing: '0.02em',
                      color: pal.nameColor, lineHeight: 1.1, marginBottom: 6,
                    }}>
                      {m.label}
                    </div>

                    {/* Range */}
                    <div style={{ fontSize: 11, fontWeight: 600, color: pal.rangeColor, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 12 }}>
                      {m.range}
                    </div>

                    {/* Count badge */}
                    <div style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      background: 'rgba(255,255,255,0.7)', borderRadius: 30,
                      padding: '4px 16px', gap: 6,
                    }}>
                      <span style={{ fontSize: 22, fontWeight: 800, color: pal.nameColor, fontFamily: 'Playfair Display, Georgia, serif' }}>{m.count}</span>
                      <span style={{ fontSize: 11, color: pal.rangeColor, fontWeight: 600 }}>volunteer{m.count !== 1 ? 's' : ''}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Milestone breakdown lists */}
            {milestoneStats.map((m, idx) => {
              const palettes = [
                { iconBg: '#10B981', nameColor: '#065F46', chipBg: '#D1FAE5', chipBorder: '#6EE7B7', chipColor: '#065F46' },
                { iconBg: '#3B82F6', nameColor: '#1E3A8A', chipBg: '#DBEAFE', chipBorder: '#93C5FD', chipColor: '#1E3A8A' },
                { iconBg: '#F59E0B', nameColor: '#78350F', chipBg: '#FEF3C7', chipBorder: '#FCD34D', chipColor: '#78350F' },
                { iconBg: '#EF4444', nameColor: '#7F1D1D', chipBg: '#FEE2E2', chipBorder: '#FCA5A5', chipColor: '#7F1D1D' },
                { iconBg: '#8B5CF6', nameColor: '#3B0764', chipBg: '#EDE9FE', chipBorder: '#C4B5FD', chipColor: '#3B0764' },
              ];
              const pal = palettes[idx] || palettes[0];
              return m.count > 0 ? (
                <div key={m.key} style={{ marginBottom: 24 }}>
                  <h4 style={{ fontSize: 15, fontWeight: 700, color: pal.nameColor, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 10, fontFamily: 'Cormorant Garamond, Playfair Display, Georgia, serif' }}>
                    <span style={{ fontSize: 20, background: pal.iconBg, borderRadius: '50%', width: 32, height: 32, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{m.icon}</span>
                    {m.label} <span style={{ fontWeight: 500, fontSize: 13, color: '#6B7280', fontFamily: 'inherit' }}>— {m.count} volunteer{m.count !== 1 ? 's' : ''}</span>
                  </h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {m.volunteers.map(v => {
                      const name = v.displayName || `${v.firstName || ''} ${v.lastName || ''}`.trim() || 'Unknown';
                      return (
                        <span key={v.id} style={{
                          background: pal.chipBg, color: pal.chipColor, border: `1px solid ${pal.chipBorder}`,
                          borderRadius: 20, padding: '4px 14px', fontSize: 12, fontWeight: 600
                        }}>
                          {name}
                          {m.threshold && (
                            <span style={{ opacity: 0.7, marginLeft: 4 }}>({v.totalHours || 0}h)</span>
                          )}
                        </span>
                      );
                    })}
                  </div>
                </div>
              ) : null;
            })}

            {milestoneStats.every(m => m.count === 0) && (
              <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF' }}>
                <p style={{ fontSize: 13 }}>No milestones earned yet.</p>
                <p style={{ fontSize: 12 }}>Milestones are awarded as volunteers log hours through shift check-ins.</p>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}
